import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  MenuItem,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { collection, query, getDocs, addDoc, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Asset, AssetState } from '../../types/Asset';
import { useAuth } from '../../contexts/AuthContext';
import PageLayout from '../../components/PageLayout';
import PageTitle from '../../components/PageTitle';
import { adminStyles } from '../../theme/theme';

const STATES: { value: AssetState; label: string }[] = [
  { value: 'new', label: 'Neuf' },
  { value: 'good', label: 'Bon état' },
  { value: 'used', label: 'Usagé' },
  { value: 'damaged', label: 'Endommagé' }
];

const Inventory = () => {
  const { user } = useAuth();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [assetForm, setAssetForm] = useState<Omit<Asset, 'id' | 'createdAt'>>({
    name: '',
    description: '',
    quantity: 1,
    state: 'new',
    inventoryDate: new Date().toISOString().split('T')[0],
    notes: '',
    createdBy: ''
  });

  useEffect(() => {
    const fetchAssets = async () => {
      const q = query(collection(db, 'inventory'));
      const querySnapshot = await getDocs(q);
      const assetsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Asset[];
      setAssets(assetsData);
    };

    fetchAssets();
  }, []);

  const handleEdit = (asset: Asset) => {
    setEditingAsset(asset);
    setAssetForm({
      name: asset.name,
      description: asset.description,
      quantity: asset.quantity,
      state: asset.state,
      inventoryDate: asset.inventoryDate,
      notes: asset.notes,
      createdBy: asset.createdBy
    });
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setEditingAsset(null);
    setAssetForm({
      name: '',
      description: '',
      quantity: 1,
      state: 'new',
      inventoryDate: new Date().toISOString().split('T')[0],
      notes: '',
      createdBy: ''
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingAsset) {
        // Mise à jour
        const assetRef = doc(db, 'inventory', editingAsset.id);
        await updateDoc(assetRef, {
          ...assetForm,
          updatedAt: new Date().toISOString()
        });
      } else {
        // Création
        await addDoc(collection(db, 'inventory'), {
          ...assetForm,
          createdAt: new Date().toISOString(),
          createdBy: user?.uid || ''
        });
      }

      // Recharger les assets
      const q = query(collection(db, 'inventory'));
      const querySnapshot = await getDocs(q);
      const assetsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Asset[];
      setAssets(assetsData);

      handleClose();
    } catch (error) {
      console.error('Error saving asset:', error);
    }
  };

  const handleDelete = async (assetId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet élément ?')) {
      try {
        await deleteDoc(doc(db, 'inventory', assetId));
        setAssets(assets.filter(asset => asset.id !== assetId));
      } catch (error) {
        console.error('Error deleting asset:', error);
      }
    }
  };

  const getStateColor = (state: AssetState) => {
    switch (state) {
      case 'damaged':
        return 'error.main';
      case 'used':
        return 'warning.main';
      case 'good':
        return 'success.main';
      default:
        return 'info.main';
    }
  };

  return (
    <PageLayout>
      <PageTitle
        title="Gestion de l'inventaire"
        icon={<AddIcon />}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
          >
            Ajouter du matériel
          </Button>
        }
      />

      <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nom</TableCell>
              <TableCell align="center">Quantité</TableCell>
              <TableCell align="center">État</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">Date d'inventaire</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assets.map((asset) => (
              <TableRow key={asset.id}>
                <TableCell>{asset.name}</TableCell>
                <TableCell align="center">{asset.quantity}</TableCell>
                <TableCell align="center">
                  <Typography color={getStateColor(asset.state)}>
                    {STATES.find(s => s.value === asset.state)?.label}
                  </Typography>
                </TableCell>
                <TableCell>{asset.description}</TableCell>
                <TableCell align="center">
                  {new Date(asset.inventoryDate).toLocaleDateString('fr-FR')}
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <IconButton
                      onClick={() => handleEdit(asset)}
                      sx={{
                        ...adminStyles.glassButton,
                        '&:hover': {
                          ...adminStyles.glassButton['&:hover'],
                          bgcolor: 'primary.main'
                        }
                      }}
                      size="small"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(asset.id)}
                      sx={{
                        ...adminStyles.glassButton,
                        '&:hover': {
                          ...adminStyles.glassButton['&:hover'],
                          bgcolor: 'error.main'
                        }
                      }}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAsset ? 'Modifier le matériel' : 'Ajouter du matériel'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Nom"
              value={assetForm.name}
              onChange={(e) => setAssetForm({ ...assetForm, name: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label="Quantité"
              type="number"
              value={assetForm.quantity}
              onChange={(e) => setAssetForm({ ...assetForm, quantity: Number(e.target.value) })}
              fullWidth
              required
              InputProps={{ inputProps: { min: 1 } }}
            />

            <TextField
              label="État"
              select
              value={assetForm.state}
              onChange={(e) => setAssetForm({ ...assetForm, state: e.target.value as AssetState })}
              fullWidth
              required
            >
              {STATES.map(state => (
                <MenuItem key={state.value} value={state.value}>
                  {state.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Description"
              value={assetForm.description}
              onChange={(e) => setAssetForm({ ...assetForm, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />

            <TextField
              label="Notes"
              value={assetForm.notes}
              onChange={(e) => setAssetForm({ ...assetForm, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            <TextField
              label="Date d'inventaire"
              type="date"
              value={assetForm.inventoryDate}
              onChange={(e) => setAssetForm({ ...assetForm, inventoryDate: e.target.value })}
              fullWidth
              required
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!assetForm.name || !assetForm.quantity || !assetForm.state}
          >
            {editingAsset ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export default Inventory;
